import React, { useState, useEffect, useCallback } from "react";
import PrincipalContainer from "./PrincipalContainer";
import TableSolicitudes from "./TableSolicitudes";
import NoSolicitudImg from "../../SVG/SinsolicitudesCenfotec.svg";
import nuevaSolicitudImg from "../../SVG/NuevasolicitudCenfotec.svg";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  obtenerSolicitudesPersona,
  obtenerEventos,
  nuevaSolicitud,
  eliminarSolicitud,
} from "../../redux/solicitudesSlice";
import OperationStatus from "../../config/OperationStatus";
import { toast } from "react-toastify";

import { getGlobal, getHomepage } from "../../Api/content";
import { useLocale } from "../../i18n/localeContext";
import { useTranslations } from "../../i18n/useTranslations";
import { Skeleton } from "antd";
import { StrapiError } from '../../Inicio/Pages/StrapiError';
import { _translateValue } from "../../Adm/translation-gestor/translateValue";

const SCREEN_NAME = "PantallaVistaInicio";

function VistaInicio() {
  const dispatch = useDispatch();
  const state = useSelector((state) => state);
  const { solicitudes } = state.solicitudes;

  // 👇 estado para global-settings
  const { locale } = useLocale();
  const { t } = useTranslations();
  const [globalSettings, setGlobalSettings] = useState(null);
  const [homepage, setHomepage] = useState(null);
  const [isLoadingContent, setIsLoadingContent] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [translatedCarreras, setTranslatedCarreras] = useState({});
  const [translatedNiveles, setTranslatedNiveles] = useState({});
  const [translatedPeriodos, setTranslatedPeriodos] = useState({});
  const [translatedEstados, setTranslatedEstados] = useState({});
  const [isTranslating, setIsTranslating] = useState(false);

    useEffect(() => {
      let cancel = false;
      setHasError(false);
      setIsLoadingContent(true);

      const timeoutId = setTimeout(() => {
        if (!cancel) {
          setGlobalSettings(null);
          setHomepage(null);
          setIsLoadingContent(false);
          setHasError(locale === 'en');
        }
      }, 5000);
      
      // Manejar cada promesa por separado para mejor control de errores
      let completed = 0;
      const total = 2;
      
      const checkCompletion = () => {
        completed++;
        if (completed === total) {
          clearTimeout(timeoutId);
          if (!cancel) {
            setIsLoadingContent(false);
          }
        }
      };
      
      getGlobal(locale)
        .then((globalRes) => {
          if (!cancel) {
            const settings = globalRes?.data?.attributes || globalRes?.data || globalRes;
            setGlobalSettings(settings);
            checkCompletion();
          }
        })
        .catch(() => {
          if (!cancel) {
            setGlobalSettings(null);
            checkCompletion();
          }
        });
        
      getHomepage(locale)
        .then((homepageRes) => {
          if (!cancel) {
            const data = homepageRes?.data?.attributes || homepageRes?.data || homepageRes;
            setHomepage(data);
            checkCompletion();
          }
        })
        .catch(() => {
          if (!cancel) {
            setHomepage(null);
            checkCompletion();
          }
        });

      return () => {
        cancel = true; 
        clearTimeout(timeoutId); 
      };
    }, [locale]);

  const cargarSolicitudes = useCallback(() => {
    dispatch(obtenerSolicitudesPersona());
  }, [dispatch]);

  useEffect(() => {
    cargarSolicitudes();
    dispatch(obtenerEventos());
  }, [cargarSolicitudes, dispatch]);

  // Efecto para traducir los campos "Programa", "Nivel", "Periodo" y "Estatus" cuando lleguen los datos o cambie el locale
  useEffect(() => {
    const translateData = async () => {
      // Verificar si hay datos para traducir
      const hasSolicitudes = solicitudes && Array.isArray(solicitudes) && solicitudes.length > 0;

      // Si no hay datos, no hacer nada
      if (!hasSolicitudes) {
        setIsTranslating(false);
        return;
      }

      // Iniciar traducción
      setIsTranslating(true);

      const carreraTranslations = {};
      const nivelTranslations = {};
      const periodoTranslations = {};
      const estadoTranslations = {};
      const translationPromises = [];

      // Recopilar todos los valores únicos que necesitan traducción
      solicitudes.forEach((item) => {
        // Traducir carrera (Programa)
        if (item.carreraNombre && !carreraTranslations[item.carreraNombre]) {
          carreraTranslations[item.carreraNombre] = null; // Marcador para evitar duplicados
          translationPromises.push(
            _translateValue(SCREEN_NAME, item.carreraNombre, locale).then((translated) => {
              carreraTranslations[item.carreraNombre] = translated;
            })
          );
        }
        
        // Traducir nivel académico
        if (item.nivelAcademicoNombre && !nivelTranslations[item.nivelAcademicoNombre]) {
          nivelTranslations[item.nivelAcademicoNombre] = null; // Marcador para evitar duplicados
          translationPromises.push(
            _translateValue(SCREEN_NAME, item.nivelAcademicoNombre, locale).then((translated) => {
              nivelTranslations[item.nivelAcademicoNombre] = translated;
            })
          );
        }
        
        // Traducir periodo
        if (item.periodo && !periodoTranslations[item.periodo]) {
          periodoTranslations[item.periodo] = null; // Marcador para evitar duplicados
          translationPromises.push(
            _translateValue(SCREEN_NAME, item.periodo, locale).then((translated) => {
              periodoTranslations[item.periodo] = translated;
            })
          );
        }
        
        // Traducir estado (Estatus)
        if (item.estadoNombre && !estadoTranslations[item.estadoNombre]) {
          estadoTranslations[item.estadoNombre] = null; // Marcador para evitar duplicados
          translationPromises.push(
            _translateValue(SCREEN_NAME, item.estadoNombre, locale).then((translated) => {
              estadoTranslations[item.estadoNombre] = translated;
            })
          );
        }
      });
      
      await Promise.all(translationPromises);

      // Actualizar estados y finalizar traducción
      setTranslatedCarreras(carreraTranslations);
      setTranslatedNiveles(nivelTranslations);
      setTranslatedPeriodos(periodoTranslations);
      setTranslatedEstados(estadoTranslations);
      setIsTranslating(false);
    };

    translateData();
  }, [solicitudes, locale]);


  const navigate = useNavigate();

  const crearSolicitud = async () => {
    let { payload } = await dispatch(nuevaSolicitud());
    if (payload.operationStatus === OperationStatus.SUCCESS) {
      toast.success(payload.message);
      navigate(`/Solicitud/${payload.result}/InfoPersonal`);
    } else {
      toast.error(payload.message);
    }
  };

  const editarSolicitud = (id) => {
    navigate(`/Solicitud/${id}/InfoPersonal`);
  };
  const verSolicitud = (id, numero) => {
    navigate(`Historial/${id}`, { state: { numero: numero } });
  };

  const removerSolicitud = async (id) => {
    let { payload } = await dispatch(eliminarSolicitud(id));

    if (payload.operationStatus === OperationStatus.SUCCESS) {
      toast.success(payload.message);
      cargarSolicitudes();
    } else {
      toast.error(payload.message);
    }
  };

  if (isLoadingContent || isTranslating) {
    return (
      <div className="principal d-flex flex-gap-20">
        <div className="container">
          <Skeleton active paragraph={{ rows: 4 }} />
          <Skeleton.Input style={{ width: '100%', height: 200 }} active />
          <Skeleton active paragraph={{ rows: 3 }} />
        </div>
        <div className="menu">
          <div className="menu-item addBtn">
            <Skeleton.Input style={{ width: 300, height: 30 }} active />
            <Skeleton.Button style={{ width: 200, height: 50 }} active />
            <Skeleton.Image style={{ width: 200, height: 150 }} />
          </div>
        </div>
      </div>
    );
  }

  if (hasError && locale === 'en') {
    return <StrapiError 
      onRetry={() => {
        setHasError(false);
        setIsLoadingContent(true);
      }}
      onUseDefault={() => {
        setHasError(false);
        setIsLoadingContent(false);
        setGlobalSettings(null);
        setHomepage(null);
      }}
    />
  }

  return (
    <>
      <div className="principal d-flex flex-gap-20">
        <PrincipalContainer
          containerId=""
          titulo={homepage?.heroSubtitle || t('solicitudes')}
          imagenId="noSolicitud"
          imagen={NoSolicitudImg}
          message={t(homepage?.noResults || "sin_solicitudes")}
          alt={t('imagen_sin_solicitudes')}
          lista={solicitudes}
          component={
            <TableSolicitudes
              lista={solicitudes}
              columns={homepage?.column}
              handleRemoveBtn={(id) => removerSolicitud(id)}
              handleViewBtn={(id, val) => verSolicitud(id, val)}
              handleEditButton={(id) => editarSolicitud(id)}
              translatedCarreras={translatedCarreras}
              translatedNiveles={translatedNiveles}
              translatedPeriodos={translatedPeriodos}
              translatedEstados={translatedEstados}
            />
          }
        />
        <div className="menu">
          <div className="menu-item addBtn">
            <h2>
              {globalSettings?.address || t('Realiza tus solicitudes de admisión de forma simple y segura.')}
            </h2>
            <button onClick={crearSolicitud}>
              <div className="d-flex flex-gap-2">
                <span>+ </span>
                <span> {homepage?.button_label || t('nueva solicitud')}</span>
              </div>
            </button>
            <img
              src={nuevaSolicitudImg}
              alt={t('imagen_nueva_solicitud')}
              id="nuevaSolicitud"
            />
          </div>
        </div>
      </div>
      <div className="popout">
        <div className="btn" onClick={crearSolicitud}>
          <i className="material-icons"></i>
        </div>
      </div>
    </>
  );
}

export default VistaInicio;
